import React, { useEffect } from 'react';
import EntityMultiSelect from '../../multi_select/EntityMultiSelect';
import Summary from '../components/Summary';
import DriverPreviewTable from '../components/Tables/DriverPreviewTable';
import BonusesModal from '../components/Modals/BonusesModal';
import StatementsModal from '../components/Modals/StatementsModal';
import WeeklyDatePicker from '../../payments/paymentsComponents/WeeklyDatePicker';
import DateRangePicker from '../../payment_platform/DateRangePicker';
import { toDateObjs, toStrings } from '../components/helpers/paymentsHelper';
import { DownArrow, SearchIcon, DownloadIcon, RefreshIcon, ICON_GREY, ToastClose, ToastError, ToastSuccess } from '../components/helpers/svgHelper';
import { IconButton, ActionButton, PP_REFRESH_GREY } from '../components/helpers/paymentControls';

function DriverMvp({
  fleets,
  week,
  setWeek,
  selectStyles,
  midMultiSelectStyles,
  fetchFleetPayments,
  loading,
  setLoading,
  entitySelectedFleets,
  onEntityFleetChange,
  downloadPdf,
  handleUpload,
  downloadTemplate,
  selectedWeek,
  isModalOpen,
  setIsModalOpen,
  modalLoading,
  isStatementsModalOpen,
  setStatementsModalOpen,
  dropdown,
  setDropdown,
  driverTable,
  fleetData,
  tripBreakdown,
  driverTripSplit,
  paymentBreakdown,
  rideshareBreakdown,
  setSelectedFile,
  selectedRows,
  setSelectedRows,
  toast,
  setToast,
  resetDriverState,
  downloadDriverCsv,
  uploadErrors,
  clearUploadErrors,
  paymentPlatformMvp,
})
  {

  const handleFileSelect = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  // Fleet methods

  useEffect(() => {
    resetDriverState();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (loading) {
        try {
          await fetchFleetPayments();
        } catch (error) {
          console.error("Error fetching fleet payments:", error);
        } finally {
          setLoading(false);
        }
      }
    };
  
    loadData();
  }, [loading]);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openStatementsModal = () => {
    setStatementsModalOpen(true);
  };

  const closeStatementsModal = () => {
    setStatementsModalOpen(false);
  };

  // "Etapath Earnings" is the drivers' gross earnings from Etapath trips — the same figure the
  // backend already returns as payment_breakdown "Total Fares" (sum of driver fares for completed and
  // late-cancelled trips). It sits alongside the third-party Uber/Bolt earnings in the Earnings card.
  const earningsBreakdown = {
    "Etapath Earnings": (paymentBreakdown && paymentBreakdown["Total Fares"]) || { main: 0, diff: null, precision: 2 },
    "Uber Earnings": rideshareBreakdown["Uber Earnings"] || { main: 0, diff: null, precision: 2 },
    "Bolt Earnings": rideshareBreakdown["Bolt Earnings"] || { main: 0, diff: null, precision: 2 },
  };

  const tripsBreakdown = {
    "Total Trips": tripBreakdown["Total Trips"] || { main: 0, diff: null, precision: 0 },
    "Employed Drivers Trips": driverTripSplit?.["Employed Drivers Trips"] || { main: 0, diff: null, precision: 0 },
    "Independent Drivers Trips": driverTripSplit?.["Independent Drivers Trips"] || { main: 0, diff: null, precision: 0 },
  };

  const driversBreakdown = {
    "Active Drivers": (fleetData && fleetData["Active Drivers"]) || { main: 0, diff: null, precision: 0 },
  };

  return (
    <>
      {loading && (
        <div className="fixed top-0 left-0 right-0 bottom-0 w-full h-screen z-50 overflow-hidden bg-gray-700 opacity-50 flex flex-col items-center justify-center">
          <div className="loader ease-linear rounded-full border-4 border-t-4 h-12 w-12 mb-4"></div>
        </div>
      )}

      {toast.show && (
        <div className={`${toast.type === "success" ? "bg-green-500" : "bg-red-400"} fixed top-50 right-50 left-50 w-[400px] p-4 shadow-lg rounded-lg flex items-center justify-between border border-gray-300 z-50`}>
          <div className="flex items-center gap-3">
            {toast.type === "success" ? (
              <ToastSuccess />
            ) : (
              <ToastError />
            )}
            <div>
              <p className="text-white font-medium">{toast.message}</p>
            </div>
          </div>
          <button onClick={() => setToast({ show: false })}>
           <ToastClose />
          </button>
        </div>
      )}
      <div className="bg-transparent pt-2 pb-4 space-y-4">
        <div className="flex flex-row items-center mb-4" style={{ gap: '10px' }}>
            <div className="flex-1 min-w-0 relative">
              <EntityMultiSelect
                availableItems={fleets}
                selectedItems={entitySelectedFleets}
                setSelectedItems={onEntityFleetChange}
                primaryField="name"
                searchFields={["name"]}
                entityLabel="Fleet"
                entityLabelPlural="Fleets"
                compact
                showChevron
                designSystem
              />
            </div>

            <div className="flex-1 min-w-0 relative text-left">
              {paymentPlatformMvp ? (
                <DateRangePicker
                  value={toDateObjs(week)}
                  onChange={(range) => setWeek(toStrings(range))}
                  maxDate={new Date()}
                  loading={loading}
                  weekOnly
                />
              ) : (
                <>
                  <div>
                    <button
                      type="button"
                      style={{ width: '150px' }}
                      onClick={() => setDropdown(!dropdown)}
                      className="inline-flex w-full justify-between items-center gap-x-1.5 border-gray-400 border rounded-md bg-white text-gray-700 px-3 py-3 text-xxs font-thin shadow-sm">
                      {week.from} - {week.to}
                      <DownArrow />
                    </button>
                  </div>
                  {dropdown && <WeeklyDatePicker setWeek={setWeek} week={week} dropdown={dropdown} setDropdown={setDropdown} />}
                </>
              )}
            </div>

            <div className="flex items-center flex-shrink-0" style={{ gap: '10px' }}>
              <IconButton
                testId="driver-overview-search-button"
                title="Search"
                disabled={entitySelectedFleets.length === 0}
                onClick={() => setLoading(true)}
                icon={<SearchIcon size={16} />}
              />
              <IconButton
                testId="driver-overview-refresh-button"
                title="Refresh"
                disabled={entitySelectedFleets.length === 0 && fleetData === null}
                onClick={resetDriverState}
                fill={PP_REFRESH_GREY}
                icon={<RefreshIcon color={ICON_GREY} size={20} />}
              />
            </div>
          <div className="flex items-center flex-shrink-0" style={{ gap: '10px' }}>
            {/* Fares Master spans all fleets, so the download depends only on a selected
                date range — not on fleet selection / fleetData. */}
            <ActionButton onClick={() => downloadDriverCsv?.(week)} disabled={!week?.from}>
              <DownloadIcon size={18} />
              Download CSV
            </ActionButton>
            <ActionButton onClick={openModal} disabled={fleetData === null}>
              Upload Bonuses &amp; Earnings
            </ActionButton>
            <ActionButton onClick={openStatementsModal} disabled={fleetData === null}>
              Statements
            </ActionButton>
          </div>
        </div>

        {entitySelectedFleets.length !== 0 && fleetData !== null && (
          <>
            <div className="flex flex-row space-x-2">
              <div className="w-5/12">
                <Summary title="Earnings" breakdown={earningsBreakdown} />
              </div>
              <div className="w-5/12">
                <Summary title="Trips" breakdown={tripsBreakdown} />
              </div>
              <div className="w-2/12">
                <Summary title="Drivers" breakdown={driversBreakdown} />
              </div>
            </div>

            <div
              className="flex flex-row p-1 rounded-lg"
              style={{ background: '#EEEDF0' }}>
            </div>
            <div
              className="overflow-y-auto px-2 scrollbar"
              style={{ maxHeight: '800px' }}
            >
              <DriverPreviewTable
                data={driverTable}
                week={week}
              />
            </div>

            {isModalOpen && entitySelectedFleets.length !== 0 && (
              <BonusesModal
                closeModal={closeModal}
                options={driverTable}
                selectedWeek={selectedWeek}
                multiSelectStyles={midMultiSelectStyles}
                selectStyles={selectStyles}
                sampleData={driverTable}
                downloadTemplate={downloadTemplate}
                handleFileSelect={handleFileSelect}
                handleUpload={handleUpload}
                modalLoading={modalLoading}
                toast={toast}
                uploadErrors={uploadErrors}
                clearUploadErrors={clearUploadErrors}
              />
            )}
            {isStatementsModalOpen && (
              <StatementsModal
                closeModal={closeStatementsModal}
                options={driverTable}
                fleets={fleets}
                selectedWeek={week.from}
                multiSelectStyles={midMultiSelectStyles}
                selectStyles={selectStyles}
                data={driverTable}
                handleGeneratePdf={downloadPdf}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                // loading={loading}
                modalLoading={modalLoading}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}

export default DriverMvp;
